import { Group, Stack } from "@mantine/core"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import {
  DEFAULT_PAGE_SIZE,
  ExportButton,
  ExportType,
  InfoBox,
  Loader,
  ColumnManager,
} from "@tfg/ui"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import z from "zod"

import { removeUndefined } from "@tfg/types/schemas/utils"

import { InlineBanner } from "~/components/InlineBanner"
import { ProductFilters } from "~/components/ProductFilters"
import { ProductSearch } from "~/components/ProductSearch"
import { useSearchParamsFilters } from "~/components/hooks/useSearchParamsFilters"
import { CustomTable } from "~/components/table/CustomTable"
import {
  FindProductsProductStandardDto,
  FindProductsStandardsRequestFiltersDto,
  FindProductsStandardsRequestSortsDto,
  useFindProductsStandards,
  useFindProductStandardValues,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { ProductStandardsSorts } from "~/pages/Brand/Products/List/Components/ProductsTables/standards/ProductStandardsSorts"
import { useStandardsTable } from "~/pages/Brand/Products/List/Components/ProductsTables/standards/useStandardsTable"
import { StandardComplianceDrawer } from "~/pages/Brand/Products/List/Components/StandardDrawer.tsx"
import { useStandardsHeaders } from "~/pages/Brand/Products/List/Components/headers.tsx"
import { ProductsListView } from "~/pages/Brand/Products/List/constants.ts"
import { useDownloadProductsCertificatesCSVMutation } from "~/pages/Brand/Products/List/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

export const StandardsProductsTable = () => {
  const view = ProductsListView.CERTIFICATES

  const { t } = useTranslation(["products", "common"])
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const [filtersValue, setFiltersValue] = useSearchParamsFilters(
    z.custom<FindProductsStandardsRequestFiltersDto>()
  )

  const [pageCount, setPageCount] = useState<number>(0)
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const fetchDataOptions = {
    pageIndex,
    pageSize,
  }
  const [sorts, setSorts] = useState<FindProductsStandardsRequestSortsDto>({})
  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
  } = useFindProductsStandards({
    filters: filtersValue,
    sorts,
    pagination: {
      pageIndex: fetchDataOptions.pageIndex + 1,
      pageSize: fetchDataOptions.pageSize,
    },
  })

  useEffect(() => {
    if (productsData?.data.totalProducts === 0) {
      setPageCount(0)
    }
    if (productsData?.data.totalProducts) {
      setPageCount(Math.ceil(productsData?.data.totalProducts / pageSize))
    }
  }, [productsData?.data.totalProducts, pageSize])

  const products = useMemo(() => {
    return productsData?.data.products ?? []
  }, [productsData])

  const standardHeaders = useStandardsHeaders(
    products,
    filtersValue?.standardId ? Number(filtersValue.standardId) : undefined
  )

  const [selectedStandard, setSelectedStandard] = useState<
    FindProductsProductStandardDto | undefined
  >(undefined)

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [filtersValue])
  const table = useStandardsTable({
    pagination,
    rowSelection,
    setPagination,
    setRowSelection,
    pageCount,
    standardHeaders,
    onStandardClick: setSelectedStandard,
    products,
  })

  const {
    mutate: exportCertificateView,
    isPending: isCertificatesExportLoading,
  } = useDownloadProductsCertificatesCSVMutation({
    filters: filtersValue,
  })
  const { data: standardValues } = useFindProductStandardValues(
    removeUndefined({ ...filtersValue, standardId: undefined })
  )

  let adminMessage: string | null = null

  if (org?.settings?.certificatesPlan !== "EXPERT") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InfoBox
          title={t("simulation.notSubscribed.title")}
          message={t("simulation.notSubscribed.message")}
          variant="info"
        />
      )
    }
  }

  return (
    <Stack>
      {adminMessage && (
        <InlineBanner
          type="admin"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {adminMessage}
        </InlineBanner>
      )}
      <Group>
        <ProductSearch
          filtersValue={filtersValue}
          setFiltersValue={setFiltersValue}
          isFetching={isFetchingProducts}
        />
        <ProductStandardsSorts sorts={sorts} setSorts={setSorts} />
        <ProductFilters
          setFiltersValue={setFiltersValue}
          filtersValue={filtersValue}
          extraFilters={[
            {
              label: t("lists.placeholders.standard"),
              key: "standardId",
              options:
                standardValues?.data.map((standard) => ({
                  label: standard.name,
                  value: standard.id.toString(),
                })) ?? [],
              type: "select",
            },
          ]}
        />
        <ColumnManager
          table={table}
          translations={columnManagerTranslations}
          isOpen={isColumnManagerOpen}
          setIsOpen={setIsColumnManagerOpen}
        />
        <Group style={{ marginLeft: "auto" }}>
          <ExportButton
            label={t("lists.export.view")}
            exportOptions={[
              {
                type: ExportType.CSV,
                label: t("common:actionButtons.exportAsCsv"),
                exportFunction: exportCertificateView,
                isLoading: isCertificatesExportLoading,
              },
            ]}
          />
        </Group>
      </Group>
      {isLoadingProducts ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          view={view}
          manualPaginationCount={productsData?.data.totalProducts || 0}
          stickyHeader
          layout="fixed"
          setOpenColumnManager={setIsColumnManagerOpen}
        />
      )}
      {selectedStandard && (
        <StandardComplianceDrawer
          standard={selectedStandard}
          onClose={() => setSelectedStandard(undefined)}
        />
      )}
    </Stack>
  )
}
